import { useState } from "react";

// ────────────────────────────────────────────────────────────────────────
// PDF Studio — Privacy Policy
// Same visual system as the Animal Escape policy page (dark "legal
// document" palette, Space Grotesk display type), content rewritten for
// PDF Studio's actual behavior: fully on-device PDF tooling, no account,
// no ads, no analytics, Google ML Kit for OCR/barcode/scanning.
//
// BEFORE DEPLOYING:
//   1. Replace CONTACT_EMAIL below with your real support address.
//   2. Replace DEVELOPER_NAME with your name/company as it should appear.
//   3. Re-read every section against what the app actually does. If you
//      add analytics, ads, or cloud sync later, this page must be updated
//      to stay accurate -- a privacy policy that doesn't match real
//      behavior is a compliance risk, not just a copy problem.
// ────────────────────────────────────────────────────────────────────────

const CONTACT_EMAIL = "alphay28071994@gmail.com"; // TODO: replace with your real support email
const DEVELOPER_NAME = "AlphaY"; // TODO: replace with your name/company

const palette = {
    bg: "#0B0F1A",
    surface: "#131929",
    raised: "#1A2235",
    border: "#1E2D44",
    accent: "#4B9EFF",
    accentDim: "#0A1A2E",
    text: "#EDF0F7",
    muted: "#7A8BA8",
    faint: "#3D4F6A",
    success: "#22C97A",
    successDim: "#0A2218",
    danger: "#FF5C7A",
    dangerDim: "#2A0E16",
    warning: "#FFB547",
    warningDim: "#251A06",
    info: "#4B9EFF",
    infoDim: "#0A1A2E",
};

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&family=Inter:wght@400;500&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html { scroll-behavior: smooth; }

  .cb-pp {
    background: ${palette.bg};
    color: ${palette.text};
    font-family: 'Inter', sans-serif;
    font-size: 15px;
    line-height: 1.7;
    min-height: 100vh;
    position: relative;
    overflow-x: hidden;
  }

  .cb-bars-bg {
    position: fixed;
    inset: 0;
    background-image: repeating-linear-gradient(
      90deg,
      ${palette.border}22 0px,
      ${palette.border}22 2px,
      transparent 2px,
      transparent 60px
    );
    pointer-events: none;
    z-index: 0;
    opacity: 0.5;
  }

  .cb-glow {
    position: fixed;
    top: -100px;
    left: 50%;
    transform: translateX(-50%);
    width: 600px;
    height: 400px;
    background: radial-gradient(ellipse, ${palette.accent}18 0%, transparent 65%);
    pointer-events: none;
    z-index: 0;
    animation: cb-pulse 5s ease-in-out infinite alternate;
  }

  @keyframes cb-pulse {
    from { opacity: 0.6; }
    to   { opacity: 1; }
  }

  .cb-wrap {
    position: relative;
    z-index: 1;
    max-width: 760px;
    margin: 0 auto;
    padding: 0 20px 80px;
  }

  .cb-hero {
    text-align: center;
    padding: 56px 0 44px;
    animation: cb-fadeup .6s ease both;
  }

  .cb-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: ${palette.accentDim};
    border: 1px solid ${palette.accent}44;
    border-radius: 100px;
    padding: 5px 14px;
    font-family: 'Space Grotesk', sans-serif;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 1.5px;
    color: ${palette.accent};
    text-transform: uppercase;
    margin-bottom: 20px;
  }

  .cb-hero h1 {
    font-family: 'Space Grotesk', sans-serif;
    font-size: clamp(30px, 6vw, 48px);
    font-weight: 700;
    letter-spacing: -1px;
    line-height: 1.1;
    margin-bottom: 12px;
  }

  .cb-hero h1 em { color: ${palette.accent}; font-style: normal; }

  .cb-hero-sub {
    color: ${palette.muted};
    font-size: 14px;
    max-width: 420px;
    margin: 0 auto 18px;
  }

  .cb-meta {
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
    gap: 8px 20px;
    color: ${palette.faint};
    font-size: 12px;
  }

  .cb-meta span {
    display: flex;
    align-items: center;
    gap: 5px;
  }

  .cb-divider {
    height: 1px;
    background: linear-gradient(90deg, transparent, ${palette.border}, transparent);
    margin: 36px 0;
  }

  .cb-section {
    background: ${palette.surface};
    border: 1px solid ${palette.border};
    border-radius: 16px;
    padding: 24px 26px 20px;
    margin-bottom: 12px;
    transition: border-color .2s;
    animation: cb-fadeup .5s ease both;
  }

  .cb-section:hover { border-color: ${palette.accent}30; }

  .cb-section-head {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 14px;
  }

  .cb-icon {
    width: 36px; height: 36px; min-width: 36px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
  }

  .cb-section h2 {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 15px;
    font-weight: 600;
    letter-spacing: -.1px;
  }

  .cb-section p, .cb-section li {
    color: ${palette.muted};
    font-size: 14px;
    line-height: 1.75;
  }

  .cb-section ul {
    padding-left: 18px;
    margin-top: 8px;
  }

  .cb-section li { margin-bottom: 5px; }

  .cb-section p + p { margin-top: 10px; }

  .cb-section a {
    color: ${palette.info};
    text-decoration: none;
    border-bottom: 1px solid ${palette.info}44;
    transition: border-color .15s;
  }

  .cb-section a:hover { border-color: ${palette.info}; }

  .cb-box {
    border-radius: 10px;
    padding: 11px 15px;
    margin-top: 14px;
    font-size: 13px;
    display: flex;
    gap: 9px;
    align-items: flex-start;
    line-height: 1.6;
  }

  .cb-box-success {
    background: ${palette.successDim};
    border: 1px solid ${palette.success}33;
    color: ${palette.success}CC;
  }

  .cb-box-warning {
    background: ${palette.warningDim};
    border: 1px solid ${palette.warning}33;
    color: ${palette.warning}CC;
  }

  .cb-box-danger {
    background: ${palette.dangerDim};
    border: 1px solid ${palette.danger}33;
    color: ${palette.danger}CC;
  }

  .cb-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
    margin: 14px 0 4px;
  }

  @media (max-width: 520px) { .cb-grid { grid-template-columns: 1fr; } }

  .cb-card {
    background: ${palette.raised};
    border: 1px solid ${palette.border};
    border-radius: 12px;
    padding: 13px 15px;
    display: flex;
    align-items: flex-start;
    gap: 11px;
  }

  .cb-card-icon {
    width: 32px; height: 32px; min-width: 32px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
  }

  .cb-card strong {
    font-size: 13px;
    font-weight: 600;
    color: ${palette.text};
    display: block;
    margin-bottom: 3px;
  }

  .cb-card p {
    font-size: 12px;
    color: ${palette.muted};
    line-height: 1.5;
    margin: 0 !important;
  }

  .cb-contact {
    background: ${palette.accentDim};
    border: 1px solid ${palette.accent}33;
    border-radius: 16px;
    padding: 32px;
    text-align: center;
    margin-top: 36px;
  }

  .cb-contact h3 {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 20px;
    font-weight: 700;
    margin-bottom: 8px;
  }

  .cb-contact p {
    color: ${palette.muted};
    font-size: 14px;
    margin-bottom: 20px;
  }

  .cb-contact-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: ${palette.accent};
    color: #0B0F1A;
    font-family: 'Space Grotesk', sans-serif;
    font-weight: 700;
    font-size: 14px;
    padding: 12px 24px;
    border-radius: 100px;
    text-decoration: none;
    transition: opacity .2s, transform .2s;
  }

  .cb-contact-btn:hover { opacity: .87; transform: translateY(-1px); }

  .cb-footer {
    text-align: center;
    padding-top: 28px;
    color: ${palette.faint};
    font-size: 12px;
  }

  @keyframes cb-fadeup {
    from { opacity: 0; transform: translateY(14px); }
    to   { opacity: 1; transform: translateY(0); }
  }
`;

const Ico = ({ e }) => <span role="img" aria-hidden="true">{e}</span>;

function SecIcon({ bg, children }) {
    return <div className="cb-icon" style={{ background: bg }}>{children}</div>;
}

function Section({ iconBg, icon, title, children, delay = 0 }) {
    return (
        <div className="cb-section" style={{ animationDelay: `${delay}s` }}>
            <div className="cb-section-head">
                <SecIcon bg={iconBg}><Ico e={icon} /></SecIcon>
                <h2>{title}</h2>
            </div>
            {children}
        </div>
    );
}

function SuccessBox({ children }) {
    return <div className="cb-box cb-box-success"><Ico e="✓" /><span>{children}</span></div>;
}

function WarnBox({ children }) {
    return <div className="cb-box cb-box-warning"><Ico e="⚠" /><span>{children}</span></div>;
}

function InfoCard({ icon, iconBg, title, desc }) {
    return (
        <div className="cb-card">
            <div className="cb-card-icon" style={{ background: iconBg }}>
                <Ico e={icon} />
            </div>
            <div>
                <strong>{title}</strong>
                <p>{desc}</p>
            </div>
        </div>
    );
}

export default function PdfStudioPrivacyPolicy() {
    const [year] = useState(new Date().getFullYear());

    return (
        <>
            <style>{css}</style>
            <div className="cb-pp">
                <div className="cb-bars-bg" />
                <div className="cb-glow" />

                <div className="cb-wrap">

                    <header className="cb-hero">
                        <div className="cb-badge">🔐 &nbsp;Legal document</div>
                        <h1>Privacy <em>Policy</em></h1>
                        <p className="cb-hero-sub">
                            How <strong>PDF Studio</strong> collects, uses, and protects your information.
                        </p>
                        <div className="cb-meta">
                            <span>🏢 Developer: {DEVELOPER_NAME}</span>
                            <span>📅 Effective: January {year}</span>
                            <span>📱 No account · No ads</span>
                        </div>
                    </header>

                    <div className="cb-divider" />

                    <Section iconBg={palette.accentDim} icon="📄" title="Overview" delay={0.05}>
                        <p>
                            <strong style={{ color: palette.text }}>PDF Studio</strong> is a fully on-device
                            PDF toolkit: viewing, editing, converting, scanning, and recognizing text all
                            happen locally on your device. This page explains what information (if any) is
                            collected and how your files are handled.
                        </p>
                        <p>By using PDF Studio, you agree to the practices described below.</p>
                        <SuccessBox>
                            PDF Studio requires no account or sign-in, and does not collect any personal
                            information about you.
                        </SuccessBox>
                    </Section>

                    <Section iconBg={palette.infoDim} icon="🗂️" title="Information we collect" delay={0.10}>
                        <p>
                            <strong style={{ color: palette.text }}>We do not operate any servers that this app sends your data to.</strong>{" "}
                            There is no account system, no analytics SDK, and no crash-reporting service
                            integrated into this app.
                        </p>
                        <p>
                            The files you work with — imported PDFs, scanned pages, extracted text, generated
                            documents — are stored in the app's private storage area on your device only.
                        </p>
                        <SuccessBox>
                            Uninstalling the app permanently deletes all of its locally stored files from your
                            device.
                        </SuccessBox>
                    </Section>

                    <Section iconBg={palette.warningDim} icon="🤖" title="On-device recognition features" delay={0.15}>
                        <p>
                            OCR (text extraction), barcode/QR scanning, and the camera document scanner are
                            all powered by <strong style={{ color: palette.text }}>Google's ML Kit</strong>.
                            Text and barcode recognition run using a model bundled in the app — your photos
                            and scanned pages are processed on your device and are not uploaded anywhere by
                            this app to perform recognition.
                        </p>
                        <p>
                            The camera document scanner specifically uses Google's Document Scanner module,
                            delivered via Google Play Services. That component is governed by Google's own
                            privacy policy.
                        </p>
                        <WarnBox>
                            Google Play Services (used only for the camera document scanner) is a Google
                            product, not part of this app —{" "}
                            <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer">
                                policies.google.com/privacy
                            </a>
                        </WarnBox>
                    </Section>

                    <Section iconBg={palette.successDim} icon="🔑" title="Permissions used" delay={0.20}>
                        <p>PDF Studio requests the following device permissions:</p>
                        <ul>
                            <li>Camera — to scan documents, capture photos for OCR/barcode reading</li>
                            <li>Photos / storage — to import files and save the PDFs/images you create</li>
                        </ul>
                        <p style={{ marginTop: 12 }}>
                            No location, contacts, microphone, or SMS access is requested.
                        </p>
                    </Section>

                    <Section iconBg="#0A1525" icon="🔌" title="Third-party components" delay={0.25}>
                        <p>PDF Studio is built using the following components, each processing data locally on your device:</p>
                        <div className="cb-grid">
                            <InfoCard
                                icon="📄" iconBg={palette.accentDim}
                                title="Syncfusion PDF SDK"
                                desc="Local PDF viewing/editing. No data collection."
                            />
                            <InfoCard
                                icon="🔤" iconBg={palette.warningDim}
                                title="Google ML Kit"
                                desc="On-device OCR & barcode recognition."
                            />
                            <InfoCard
                                icon="📷" iconBg="#0A1A10"
                                title="Google Play Services"
                                desc="Powers the camera document scanner only."
                            />
                            <InfoCard
                                icon="📁" iconBg={palette.infoDim}
                                title="System file/photo pickers"
                                desc="Native pickers — files chosen stay on-device."
                            />
                            <InfoCard
                                icon="📤" iconBg="#1A100A"
                                title="Device share sheet"
                                desc="Sharing hands off to apps you choose."
                            />
                            <InfoCard
                                icon="💾" iconBg={palette.successDim}
                                title="Local device storage"
                                desc="All files stay in this app's private storage."
                            />
                        </div>
                    </Section>

                    <Section iconBg="#0A1525" icon="🕓" title="Data retention" delay={0.30}>
                        <p>
                            Files remain in the app's storage until you delete them yourself (via My Files)
                            or uninstall the app, at which point the operating system removes them
                            automatically.
                        </p>
                        <p>We do not maintain any server-side copy of your files.</p>
                    </Section>

                    <Section iconBg={palette.dangerDim} icon="🧒" title="Children's privacy" delay={0.35}>
                        <p>
                            PDF Studio is a general-purpose productivity tool and is not directed at children.
                            Since the app collects no personal information from any user, none is knowingly
                            collected from children either.
                        </p>
                        <p>
                            If you believe a child has been affected in a way that concerns you, please
                            contact us at{" "}
                            <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
                        </p>
                    </Section>

                    <Section iconBg={palette.successDim} icon="🛡️" title="Security" delay={0.40}>
                        <p>
                            Your files live in this app's private, sandboxed storage on your device,
                            inaccessible to other apps unless you explicitly share or export them.
                        </p>
                        <p>
                            For sensitive documents, use the app's built-in "Protect PDF" tool to add a
                            password, and keep your device itself secured with a passcode or biometric lock.
                        </p>
                    </Section>

                    <Section iconBg={palette.warningDim} icon="🔄" title="Changes to this policy" delay={0.45}>
                        <p>
                            We may update this privacy policy from time to time. Changes will be posted on
                            this page with an updated effective date at the top.
                        </p>
                        <p>
                            Continued use of PDF Studio after changes are posted constitutes acceptance of
                            the updated policy.
                        </p>
                    </Section>

                    <Section iconBg={palette.successDim} icon="✅" title="Your consent" delay={0.50}>
                        <p>
                            By using PDF Studio, you consent to this privacy policy. If you do not agree,
                            please discontinue use of the app.
                        </p>
                    </Section>

                    <div className="cb-contact">
                        <h3>Questions or concerns?</h3>
                        <p>
                            If you have any questions about how PDF Studio handles your data, don't hesitate
                            to get in touch.
                        </p>
                        <a href={`mailto:${CONTACT_EMAIL}`} className="cb-contact-btn">
                            ✉️ &nbsp;{CONTACT_EMAIL}
                        </a>
                    </div>

                    <div className="cb-divider" />
                    <footer className="cb-footer">
                        <p>© {year} <strong style={{ color: palette.muted }}>{DEVELOPER_NAME}</strong> · PDF Studio · All rights reserved.</p>
                        <p style={{ marginTop: 5 }}>This app is not affiliated with or endorsed by Google LLC or Syncfusion Inc.</p>
                    </footer>

                </div>
            </div>
        </>
    );
}